import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Form,
  FormFeedback,
} from 'reactstrap';
import moment from 'moment';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Select from 'react-select';

import TableContainer from '../../Components/Common/TableContainer';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  addNewUser,
  editUserInfo,
  getAllUsers,
  removeUser,
} from 'services/userService';
import { useProfile } from 'Components/Hooks/UserHooks';
import { useDebounce } from 'hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { AccessLevels, User } from 'utility/interface';
import { getAllOrganization } from 'services/orgService';
import ConfirmDialog from 'Components/Common/ConfirmDialog';

// Yup Validation Schema
const validationSchema = Yup.object().shape({
  organizationId: Yup.number().required('Please select a Mazar'),
  username: Yup.string().required('Please Enter Username'),
  name: Yup.string().required('Please Enter your Full Name'),
  email: Yup.string().email('Please enter your valid email'),
  password: Yup.string()
    .min(4, 'Password must be at least 4 characters')
    .required('Please enter your Password'),
  phone: Yup.string()
    .notRequired()
    .test('is-valid-phone', 'Phone number must be 10 digits', function (value) {
      if (!value || value.trim().length === 0) return true; // skip validation if empty
      return /^[0-9]{10}$/.test(value);
    }),
  accessLevels: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required(),
      }),
    )
    .min(1, 'Please select at least one access level')
    .required('Please select access level'),
});

const Users = () => {
  const { id } = useParams();
  const { userProfile } = useProfile();
  const [user, setUser] = useState<User>();
  const [isEdit, setIsEdit] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      organizationId:
        Number(id) ||
        (!user?.accessLevels?.includes(AccessLevels.SUPER_ADMIN) &&
          user?.organizationId),
      username: '',
      name: '',
      email: '',
      password: '',
      phone: '',
      accessLevels: [],
    },
  });

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setIsEdit(false);
      setEditUser(null);
    } else {
      setModal(true);
    }
    reset();
  }, [modal]);

  const accessLevelOptions = Object.entries(AccessLevels)
    // .filter(([key]) => key !== 'SUPER_ADMIN')
    .map(([, value]) => ({
      label: value,
      value,
    }));

  const loadData = async (search = '') => {
    dispatch(isVisible(true));
    try {
      const authUser = sessionStorage.getItem('authUser');
      if (authUser) {
        const parsedUser = JSON.parse(authUser);
        setUser(parsedUser);
        const usersResult = await getAllUsers(
          !parsedUser.accessLevels.includes(AccessLevels.SUPER_ADMIN)
            ? parsedUser.organizationId
            : Number(id),
          search,
        );

        if (usersResult?.data?.rows?.length) {
          const list = usersResult?.data?.rows?.map(user => user);
          setUsers(list);
        }
      }
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      toast.error(`Error in getAllUsers: ${error}`, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  useEffect(() => {
    const loadData = async (search = '') => {
      dispatch(isVisible(true));
      try {
        const authUser = sessionStorage.getItem('authUser');
        if (authUser) {
          const parsedUser = JSON.parse(authUser);
          setUser(parsedUser);
          const result = await getAllOrganization(null, search, parsedUser.id);
          if (result?.data?.rows?.length) {
            const list = result?.data?.rows?.map(org => org);
            setOrganizations(list);
          } else {
            setOrganizations([]);
          }
        }
      } catch (error) {
        console.error('Error fetching Mazar:', error);
        toast.error(`Error fetching Mazar: ${error}`, {
          autoClose: 2000,
        });
        setOrganizations([]);
      } finally {
        dispatch(isVisible(false));
      }
    };

    loadData();
  }, []);

  const onSubmit = async values => {
    dispatch(isVisible(true));
    try {
      const newUser = {
        organizationId:
          id ||
          values.organizationId ||
          (!user?.accessLevels?.includes(AccessLevels.SUPER_ADMIN) &&
            user?.organizationId),
        username: values.username,
        name: values.name,
        email: values.email || null,
        password: values.password,
        phone: values.phone || null,
        accessLevels: values.accessLevels?.map(access => access?.value),
      };

      if (isEdit) {
        const result = await editUserInfo(editUser?.id, newUser);

        if (result?.data)
          toast.success('User Updated Successfully', { autoClose: 2000 });
      } else {
        const result = await addNewUser(userProfile?.id, newUser);

        if (result?.data)
          toast.success('User Added Successfully', { autoClose: 2000 });
      }

      await loadData();
      toggle();
      reset();
    } catch (error) {
      console.error('Error in onSubmit:', error);
      toast.error(`Error in onSubmit: ${error}`, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const onClickEdit = user => {
    setEditUser(user);
    setIsEdit(true);
    setModal(true);
  };

  const onClickDelete = user => {
    setDeleteUser(user);
    setDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (deleteUser) {
      dispatch(isVisible(true));
      try {
        await removeUser(userProfile?.id, deleteUser?.id);
        setDeleteUser(null);
        setDeleteModal(false);
        await loadData();
      } catch (error) {
        console.error('Error in handleDeleteUser:', error);
        toast.error(`Error in handleDeleteUser: ${error}`, {
          autoClose: 2000,
        });
      } finally {
        dispatch(isVisible(false));
      }
    }
  };

  useEffect(() => {
    if (editUser) {
      const accessList = JSON.parse(editUser.accessLevels);

      reset({
        organizationId:
          !user?.accessLevels?.includes(AccessLevels.SUPER_ADMIN) &&
          user?.organizationId,
        username: editUser.username || '',
        name: editUser.name || '',
        email: editUser.email || '',
        password: editUser.password || '',
        phone: editUser.phone || '',
        accessLevels: accessLevelOptions.filter(access =>
          accessList.includes(access.value),
        ),
      });
    } else {
      reset({
        organizationId:
          !user?.accessLevels?.includes(AccessLevels.SUPER_ADMIN) &&
          user?.organizationId,
        username: '',
        name: '',
        email: '',
        password: '',
        phone: '',
        accessLevels: [],
      });
    }
  }, [editUser, reset]);

  useEffect(() => {
    loadData(debouncedSearchQuery);
  }, [debouncedSearchQuery, pathname]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Columns for TableContainer (unchanged)
  const columns = useMemo(
    () => [
      {
        header: 'Username',
        accessorKey: 'username',
        enableColumnFilter: false,
        cell: cell => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 ms-2 name">{cell.getValue()}</div>
          </div>
        ),
      },
      { header: 'Full Name', accessorKey: 'name', enableColumnFilter: false },
      { header: 'Email', accessorKey: 'email', enableColumnFilter: false },
      {
        header: 'Mazar Name',
        accessorKey: 'organization',
        enableColumnFilter: false,
      },
      {
        header: 'Role',
        accessorKey: 'accessLevels',
        enableColumnFilter: false,
        cell: cell => JSON.parse(cell.getValue()).join(', '),
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        enableColumnFilter: false,
        cell: cell => moment(new Date(cell.getValue())).format('DD MMM Y'),
      },
      {
        header: 'Action',
        cell: cellProps => (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
              <Link
                to="#"
                onClick={() => {
                  onClickEdit(cellProps.row.original);
                }}
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
              </Link>
            </li>
            <li className="list-inline-item" title="Delete">
              <Link
                className="remove-item-btn"
                onClick={() => onClickDelete(cellProps.row.original)}
                to="#"
              >
                <i className="ri-delete-bin-fill align-bottom text-muted"></i>
              </Link>
            </li>
          </ul>
        ),
      },
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <ConfirmDialog
          isOpen={deleteModal}
          title="Confirm Delete"
          message="Are you sure you want to delete this user?"
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteUser}
          onCancel={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb
            title="Users"
            breadcrumbItems={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Users', isActive: true },
            ]}
          />
          <Row>
            <Col lg={12}>
              <Card id="usersList overflow-hidden">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm-auto">
                      <div className="hstack gap-2">
                        <button
                          type="button"
                          className="btn btn-primary add-btn"
                          onClick={() => {
                            setIsEdit(false);
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i> Add
                          User
                        </button>
                      </div>
                    </div>
                    <div className="col-sm-3 ms-auto">
                      <div className="d-flex justify-content-sm-end gap-2">
                        <div className="search-box ms-2 col-sm-10">
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Search users"
                            value={searchQuery}
                            onChange={handleSearch}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="pt-3">
                  {
                    users && users.length ? (
                      <TableContainer
                        columns={columns}
                        data={users || []}
                        isGlobalFilter={false}
                        customPageSize={10}
                        divClass="table-responsive table-card"
                        tableClass="align-middle"
                        theadClass="table-light"
                        isLeadsFilter={false}
                      />
                    ) : null
                    // <Loader error={error} />
                  }
                  <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {isEdit ? 'Edit User' : 'Add User'}
                    </ModalHeader>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                      <ModalBody className="overflow-hidden">
                        <Row className="g-3">
                          {!id &&
                            user?.accessLevels?.includes(
                              AccessLevels.SUPER_ADMIN,
                            ) && (
                              <Col lg={12}>
                                <Label
                                  htmlFor="organizationId-field"
                                  className="form-label"
                                >
                                  Mazar *
                                </Label>
                                <Controller
                                  name="organizationId"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...field },
                                  }) => (
                                    <Input
                                      {...field}
                                      type="select"
                                      id="organization-field"
                                      className="form-control"
                                      invalid={!!errors.organizationId}
                                      value={value ?? id}
                                      onChange={e => {
                                        const selected = e.target.value;
                                        onChange(
                                          selected ? Number(selected) : null,
                                        );
                                      }}
                                    >
                                      <option value="">Select a Mazar</option>
                                      {organizations.map(org => (
                                        <option key={org.id} value={org.id}>
                                          {org.organization}
                                        </option>
                                      ))}
                                    </Input>
                                  )}
                                />
                                {errors.organizationId && (
                                  <FormFeedback>
                                    {errors.organizationId.message}
                                  </FormFeedback>
                                )}
                              </Col>
                            )}
                          <Col lg={12}>
                            <Label
                              htmlFor="username-field"
                              className="form-label"
                            >
                              Username*
                            </Label>
                            <Controller
                              name="username"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  id="username-field"
                                  className="form-control"
                                  placeholder="Enter Username"
                                  invalid={!!errors.username}
                                />
                              )}
                            />
                            {errors.username && (
                              <FormFeedback>
                                {errors.username.message}
                              </FormFeedback>
                            )}
                          </Col>
                          <Col lg={12}>
                            <Label
                              htmlFor="fullname-field"
                              className="form-label"
                            >
                              Full Name*
                            </Label>
                            <Controller
                              name="name"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  id="name-field"
                                  className="form-control"
                                  placeholder="Enter Full Name"
                                  invalid={!!errors.name}
                                />
                              )}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </Col>
                          <Col lg={12}>
                            <Label
                              htmlFor="address-field"
                              className="form-label"
                            >
                              Enter Email
                            </Label>
                            <Controller
                              name="email"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  id="email-field"
                                  className="form-control"
                                  placeholder="Enter Email"
                                  invalid={!!errors.email}
                                />
                              )}
                            />
                            {errors.email && (
                              <FormFeedback>
                                {errors.email.message}
                              </FormFeedback>
                            )}
                          </Col>
                          {!isEdit ? (
                            <Col lg={12}>
                              <Label
                                htmlFor="password-field"
                                className="form-label"
                              >
                                Enter Password*
                              </Label>
                              <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="password-field"
                                    className="form-control"
                                    placeholder="Enter Password"
                                    type="password"
                                    invalid={!!errors.password}
                                  />
                                )}
                              />
                              {errors.password && (
                                <FormFeedback>
                                  {errors.password.message}
                                </FormFeedback>
                              )}
                            </Col>
                          ) : null}
                          <Col lg={6}>
                            <Label htmlFor="phone-field" className="form-label">
                              Enter Phone
                            </Label>
                            <Controller
                              name="phone"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  id="phone-field"
                                  className="form-control"
                                  placeholder="Enter Phone"
                                  invalid={!!errors.phone}
                                />
                              )}
                            />
                            {errors.phone && (
                              <FormFeedback>
                                {errors.phone.message}
                              </FormFeedback>
                            )}
                          </Col>
                          <Col lg={6}>
                            <Label
                              htmlFor="accessLevels-field"
                              className="form-label"
                            >
                              Select Access Levels*
                            </Label>
                            <Controller
                              name="accessLevels"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  id="accessLevels-field"
                                  isMulti
                                  options={accessLevelOptions}
                                  className="basic-multi-select"
                                  classNamePrefix="select"
                                  styles={{
                                    multiValue: styles => ({
                                      ...styles,
                                      backgroundColor: '#007bff', // Change to your preferred color
                                    }),
                                    multiValueLabel: styles => ({
                                      ...styles,
                                      color: 'white', // Set text color to white
                                    }),
                                    multiValueRemove: styles => ({
                                      ...styles,
                                      color: 'white', // Set close (×) icon color to white
                                      ':hover': {
                                        backgroundColor: '#0056b3', // Darker shade on hover
                                        color: 'white',
                                      },
                                    }),
                                  }}
                                  onChange={selected => {
                                    const lastSelected =
                                      selected?.[selected.length - 1];
                                    const hasAdmin = selected?.some(
                                      option => option.value === 'Admin',
                                    );
                                    const hasSuperAdmin = selected?.some(
                                      option => option.value === 'Super Admin',
                                    );

                                    if (
                                      lastSelected?.value === 'Admin' ||
                                      lastSelected?.value === 'Super Admin'
                                    ) {
                                      // If the last picked option is Admin or Super Admin, keep only that
                                      field.onChange([lastSelected]);
                                    } else if (hasAdmin || hasSuperAdmin) {
                                      // If Admin or Super Admin is already selected, don't allow adding more
                                      field.onChange(field.value);
                                    } else {
                                      // Normal options — allow multiple
                                      field.onChange(selected ?? []);
                                    }
                                  }}
                                  value={field.value}
                                />
                              )}
                            />
                            {errors.accessLevels && (
                              <FormFeedback className="d-block">
                                {errors.accessLevels.message}
                              </FormFeedback>
                            )}
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <div className="hstack gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={toggle}
                          >
                            Close
                          </button>
                          <button type="submit" className="btn btn-success">
                            {isEdit ? 'Update' : 'Create'}
                          </button>
                        </div>
                      </ModalFooter>
                    </Form>
                  </Modal>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Users;
